import type { EnemyType } from "../entities/enemy.ts";

/**
 * Score & stat tracking. Longevity in FLOWSTATE comes from mechanical mastery:
 * surviving cleanly (no-hit) and grazing aggressively build score. The
 * multiplier rewards unbroken survival: every 20 seconds without taking a hit
 * it steps up ×1 → ×2 → ×3 … (uncapped), scaling all points earned from then
 * on. Taking a hit snaps it back to ×1. There's no letter grade — a clean,
 * no-hit clear is simply called out as a PERFECT run.
 */

// Kill value by threat, indexed by EnemyType (METEOR/ARCHER/BOMBER/HEN/BEAST/
// LASER/NINJA): simple hazards pay out least, tougher and more dangerous
// enemies pay out most.
const ENEMY_KILL_POINTS: readonly number[] = [100, 200, 400, 150, 350, 250, 300];

export interface RunResult {
  score: number;
  kills: number;
  hits: number;
  timeSurvived: number;
  cleared: boolean;
  perfect: boolean;
}

/** Counter values captured at a moment in time (used for per-level deltas). */
export interface ScoreSnapshot {
  score: number;
  kills: number;
  hits: number;
}

export class Scoring {
  score = 0;
  grazes = 0;
  kills = 0;
  hits = 0;
  multiplier = 1;
  /** Seconds survived since the last hit — drives the multiplier. */
  private streak = 0;

  reset(): void {
    this.score = 0;
    this.grazes = 0;
    this.kills = 0;
    this.hits = 0;
    this.multiplier = 1;
    this.streak = 0;
  }

  /**
   * Survival drip + the no-hit multiplier: a small score trickle keeps the
   * meter alive while dodging, and every 20 unhit seconds steps the
   * multiplier up another notch.
   */
  tick(dt: number): void {
    this.streak += dt;
    this.multiplier = 1 + Math.floor(this.streak / 20);
    this.score += 8 * this.multiplier * dt;
  }

  graze(): void {
    this.grazes += 1;
    this.score += 25 * this.multiplier;
  }

  hit(): void {
    this.hits += 1;
    this.streak = 0;
    this.multiplier = 1;
  }

  /** Sword rewards: cut bullets, wound enemies, finish them off. */
  slash(enemyHits: number, bulletsCut: number): void {
    this.score += (enemyHits * 40 + bulletsCut * 5) * this.multiplier;
  }

  enemyKill(type: EnemyType): void {
    this.kills += 1;
    this.score += ENEMY_KILL_POINTS[type] * this.multiplier;
  }

  result(timeSurvived: number, cleared: boolean): RunResult {
    return {
      score: Math.floor(this.score),
      kills: this.kills,
      hits: this.hits,
      timeSurvived,
      cleared,
      perfect: cleared && this.hits === 0,
    };
  }

  /** Capture current counters (taken at level start for per-level results). */
  snapshot(): ScoreSnapshot {
    return { score: this.score, kills: this.kills, hits: this.hits };
  }

  /** A cleared-level result computed as the delta since `base` — score
   * earned, KO's landed, and hits taken within just that level. */
  resultSince(base: ScoreSnapshot, timeSurvived: number): RunResult {
    const hits = this.hits - base.hits;
    return {
      score: Math.floor(this.score - base.score),
      kills: this.kills - base.kills,
      hits,
      timeSurvived,
      cleared: true,
      perfect: hits === 0,
    };
  }
}
